const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_PROJECTION_MONTHS = 600;

const numberOf = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const isoDate = (date) => date.toISOString().slice(0, 10);
const roundMoney = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

const rateForDate = (history, date, fallbackRate) => {
  const key = isoDate(date);
  const applicable = [...(history || [])]
    .reverse()
    .find((entry) => entry.effectiveFrom <= key);
  return numberOf(applicable?.rate ?? fallbackRate);
};

const addMonth = (date) => {
  const next = new Date(date);
  const day = next.getUTCDate();
  next.setUTCDate(1);
  next.setUTCMonth(next.getUTCMonth() + 1);
  const lastDay = new Date(
    Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 0),
  ).getUTCDate();
  next.setUTCDate(Math.min(day, lastDay));
  return next;
};

export const calculateCurrentRepaymentInstalment = ({
  chargeFrom,
  repaymentInstalments,
  asOf = new Date(),
}) => {
  const start = new Date(`${chargeFrom}T00:00:00.000Z`);
  const total = Math.max(0, Math.trunc(numberOf(repaymentInstalments)));
  const reference = asOf instanceof Date ? asOf : new Date(asOf);
  if (!total || Number.isNaN(start.getTime()) || Number.isNaN(reference.getTime())) {
    return { current: 0, total, label: "" };
  }

  const elapsedMonths =
    (reference.getUTCFullYear() - start.getUTCFullYear()) * 12 +
    reference.getUTCMonth() -
    start.getUTCMonth();
  const current =
    elapsedMonths < 0 ? 0 : Math.min(total, Math.max(1, elapsedMonths + 1));
  return { current, total, label: `${current}/${total}` };
};

export const calculateLoanChargeTo = (chargeFrom, repaymentInstalments) => {
  const start = new Date(`${chargeFrom}T00:00:00.000Z`);
  const instalments = Math.trunc(numberOf(repaymentInstalments));
  if (Number.isNaN(start.getTime()) || instalments <= 0) return "";

  const end = new Date(start);
  end.setUTCDate(1);
  end.setUTCMonth(end.getUTCMonth() + instalments);
  const startDay = start.getUTCDate();
  const lastDayOfTargetMonth = new Date(
    Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 0),
  ).getUTCDate();
  end.setUTCDate(Math.min(startDay, lastDayOfTargetMonth));
  end.setUTCDate(end.getUTCDate() - 1);
  return isoDate(end);
};

export const calculateLoanRepaymentSchedule = ({
  totalAmount,
  chargeFrom,
  annualInterestRatePa,
  annualBoeBaseRatePa,
  monthlyRepaymentAmount,
  currentMonthlyRepaymentAmount,
  bankRateHistory = [],
}) => {
  const principal = numberOf(totalAmount);
  const expectedPayment = numberOf(monthlyRepaymentAmount);
  const actualPayment = numberOf(currentMonthlyRepaymentAmount) || expectedPayment;
  const marginRate = numberOf(annualInterestRatePa);
  const fallbackBoeRate = numberOf(annualBoeBaseRatePa);
  const start = new Date(`${chargeFrom}T00:00:00.000Z`);

  if (
    principal <= 0 ||
    actualPayment <= 0 ||
    Number.isNaN(start.getTime())
  ) {
    return {
      schedule: [],
      totalRepaymentAmount: 0,
      totalInterest: 0,
      fullyRepaid: false,
    };
  }

  const schedule = [];
  let balance = principal;
  let periodStart = new Date(start);
  let periodEndExclusive = addMonth(start);
  let paymentDate = new Date(periodEndExclusive.getTime() - DAY_MS);
  let totalInterest = 0;
  let totalRepaymentAmount = 0;

  for (
    let instalment = 1;
    balance > 0.005 && instalment <= MAX_PROJECTION_MONTHS;
    instalment += 1
  ) {
    const openingBalance = balance;
    let interestAccrued = 0;
    let cursor = new Date(periodStart);
    let lastBoeRate = fallbackBoeRate;

    while (cursor < periodEndExclusive) {
      lastBoeRate = rateForDate(bankRateHistory, cursor, fallbackBoeRate);
      const annualRate = marginRate + lastBoeRate;
      interestAccrued += balance * (annualRate / 100) / 365;
      cursor = new Date(cursor.getTime() + DAY_MS);
    }

    interestAccrued = roundMoney(interestAccrued);
    const paymentDue = roundMoney(balance + interestAccrued);
    const payment = Math.min(actualPayment, paymentDue);
    const appliedToInterest = Math.min(payment, interestAccrued);
    const appliedToPrincipal = Math.min(
      balance,
      Math.max(0, payment - appliedToInterest),
    );
    balance = roundMoney(balance - appliedToPrincipal);
    totalInterest = roundMoney(totalInterest + interestAccrued);
    totalRepaymentAmount = roundMoney(totalRepaymentAmount + payment);

    schedule.push({
      instalment,
      periodFrom: isoDate(periodStart),
      paymentDate: isoDate(paymentDate),
      openingBalance: roundMoney(openingBalance),
      bankOfEnglandRate: lastBoeRate,
      annualInterestRateApplied: roundMoney(marginRate + lastBoeRate),
      interestAccrued,
      expectedMonthlyRepayment: expectedPayment,
      actualMonthlyRepayment: payment,
      appliedToInterest: roundMoney(appliedToInterest),
      appliedToPrincipal: roundMoney(appliedToPrincipal),
      closingBalance: balance,
    });

    if (payment <= interestAccrued && balance > 0) break;
    periodStart = new Date(periodEndExclusive);
    periodEndExclusive = addMonth(periodStart);
    paymentDate = new Date(periodEndExclusive.getTime() - DAY_MS);
  }

  return {
    schedule,
    totalRepaymentAmount,
    totalInterest,
    fullyRepaid: balance <= 0.005,
  };
};
